import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Typography, Layout, Select, Button, message, Table, Tag, Tabs, Empty } from 'antd';

import './SelfcareDashboardView.css';
import { apiUrl } from '../../../utility/Connections';
import LoadingOverlay from '../../../components/loadingOverlay/loadingOverlay';
import { getFromLocalStorage } from '../../../helpers/EncryptData';
import LeadershipSummary from '../../../components/informs/components/leadershipSummary/leadershipSummary';
import ManagerialCrossDashboard from '../../../components/informs/managerialCrossDashboard/ManagerialCrossDashboard';
import { getGlobalAverage } from '../../../utils/informUtils';

const { Title } = Typography;
const { Content } = Layout;
const { Option } = Select;

const DEFAULT_SRV_IDS = [6, 7];

const SelfcareDashboardView = ({ title = 'Dashboard' }) => {
    const [showLoading, setShowLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');

    const [dataEvaluations, setDataEvaluations] = useState([]);
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);
    const [iashEvaluations, setIashEvaluations] = useState([]);
    const [ipraEvaluations, setIpraEvaluations] = useState([]);
    const [selectedIashEvaluationId, setSelectedIashEvaluationId] = useState(null);
    const [selectedIpraEvaluationId, setSelectedIpraEvaluationId] = useState(null);
    const [managerialRuns, setManagerialRuns] = useState([]);
    const [selectedManagerialRunId, setSelectedManagerialRunId] = useState(null);
    const [selectedManagerialReport, setSelectedManagerialReport] = useState(null);
    const [managerialOrgLogo, setManagerialOrgLogo] = useState(null);
    const [managerialReportLoading, setManagerialReportLoading] = useState(false);
    const [dataDiagnostics, setDataDiagnostics] = useState({
        iashEvaluations: 0,
        ipraEvaluations: 0,
        runs: 0,
        lastListError: null,
    });

    const [totalResults, setTotalResults] = useState({});

    const headers = useMemo(() => {
        const token = localStorage.getItem('access_token');
        return {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
    }, []);

    useEffect(() => {
        const currentOrg = getFromLocalStorage('current_org');
        if (currentOrg) {
            getEvaluations(currentOrg.org_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getEvaluationsBySrv = async (orgId, srvId) => {
        try {
            const response = await axios.get(`${apiUrl}/evaluations/get-full-evaluations/${orgId}/${srvId}/`, headers);
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            return [];
        }
    };

    const getOrganizationLogo = async (orgId) => {
        try {
            const response = await axios.get(`${apiUrl}/organizations/get-organization-logo/${orgId}/`);
            return response.data?.org_logo || null;
        } catch (error) {
            return null;
        }
    };

    const getManagerialCrossReport = async (gcrId, options = {}) => {
        const { silent = false } = options;

        if (!gcrId) {
            setSelectedManagerialRunId(null);
            setSelectedManagerialReport(null);
            setManagerialOrgLogo(null);
            return;
        }

        setSelectedManagerialRunId(gcrId);
        setManagerialReportLoading(true);

        try {
            const response = await axios.get(`${apiUrl}/informs/get-managerial-cross-report/${gcrId}/`, headers);
            const data = response.data;
            setSelectedManagerialReport(data);

            const orgId = data?.org_id || data?.payload?.evaluations?.ipra?.org_id || data?.payload?.evaluations?.iash?.org_id;
            setManagerialOrgLogo(orgId ? await getOrganizationLogo(orgId) : null);
        } catch (error) {
            setSelectedManagerialReport(null);
            setManagerialOrgLogo(null);
            if (!silent) {
                message.error('No fue posible cargar la corrida seleccionada');
            }
        } finally {
            setManagerialReportLoading(false);
        }
    };

    const getManagerialCrossRuns = async (orgId) => {
        try {
            const response = await axios.get(`${apiUrl}/informs/list-managerial-cross-reports/${orgId}/`, headers);
            const rows = [...(response.data?.results || [])].sort((left, right) => {
                const rightDate = new Date(right?.created_at || 0).getTime();
                const leftDate = new Date(left?.created_at || 0).getTime();
                return rightDate - leftDate;
            });

            setManagerialRuns(rows);
            setDataDiagnostics((prev) => ({
                ...prev,
                runs: rows.length,
                lastListError: null,
            }));

            return rows;
        } catch (error) {
            setManagerialRuns([]);
            setDataDiagnostics((prev) => ({
                ...prev,
                runs: 0,
                lastListError: error?.response?.data?.error || 'No fue posible consultar las corridas gerenciales',
            }));

            return [];
        }
    };

    const getEvaluations = async (orgId) => {
        setShowLoading(true);
        setLoadingText('Cargando información...');

        const results = await Promise.all(DEFAULT_SRV_IDS.map((srvId) => getEvaluationsBySrv(orgId, srvId)));
        const iashData = results[0] || [];
        const ipraData = results[1] || [];

        setIashEvaluations(iashData);
        setIpraEvaluations(ipraData);
        setDataDiagnostics((prev) => ({
            ...prev,
            iashEvaluations: iashData.length,
            ipraEvaluations: ipraData.length,
        }));

        if (iashData.length > 0) {
            setSelectedIashEvaluationId(iashData[iashData.length - 1].evl_id);
        }

        if (ipraData.length > 0) {
            setSelectedIpraEvaluationId(ipraData[ipraData.length - 1].evl_id);
        }

        const merged = results.flat();
        const byId = new Map();
        for (const evl of merged) {
            if (evl?.evl_id != null) {
                byId.set(evl.evl_id, evl);
            }
        }

        const ordered = Array.from(byId.values()).sort((a, b) => (a.evl_id || 0) - (b.evl_id || 0));
        setDataEvaluations(ordered);

        if (ordered.length > 0) {
            const lastEvaluation = ordered[ordered.length - 1];
            setSelectedEvaluation(lastEvaluation);
            await getEvaluationResults(lastEvaluation.evl_id);
        }

        const runs = await getManagerialCrossRuns(orgId);
        if (runs.length > 0) {
            await getManagerialCrossReport(runs[0].gcr_id, { silent: true });
        }

        setShowLoading(false);
    };

    const getEvaluationResults = async (evlId) => {
        setShowLoading(true);
        setLoadingText('Cargando información. Esto puede tardar un poco...');

        try {
            const response = await axios.get(`${apiUrl}/informs/get-evaluation-results/${evlId}/`, headers);
            setTotalResults(response.data?.totals || {});
        } catch (error) {
            setTotalResults({});
        } finally {
            setShowLoading(false);
        }
    };

    const handleOnChangeEvaluation = async (evlId) => {
        const selected = dataEvaluations.find((evl) => evl.evl_id === evlId);
        if (!selected) return;

        setSelectedEvaluation(selected);
        await getEvaluationResults(selected.evl_id);
    };

    const seeGeneralInform = () => {
        if (!selectedEvaluation?.evl_id) return;

        const rootUrl = window.location.origin;
        window.open(`${rootUrl}/informes/general/${selectedEvaluation.evl_id}`, '_blank');
    };

    const downloadEvaluationExcel = async () => {
        if (!selectedEvaluation?.evl_id) return;

        setShowLoading(true);
        setLoadingText('Generando archivo. Esto puede tardar un poco...');

        try {
            const token = localStorage.getItem('access_token');
            const excelHeaders = {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: 'blob',
            };

            const response = await axios.get(`${apiUrl}/informs/export-evaluation-results-excel/${selectedEvaluation.evl_id}/`, excelHeaders);
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Resultados ${selectedEvaluation.evl_title}.xlsx`);
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
            message.success('Archivo descargado');
        } catch (error) {
            message.error('Error exportando el archivo');
        } finally {
            setShowLoading(false);
        }
    };

    const createManagerialCrossReport = async () => {
        if (!selectedIashEvaluationId) {
            message.warning('Selecciona una evaluación IASH');
            return;
        }

        if (!selectedIpraEvaluationId) {
            message.warning('Selecciona una evaluación IPRA');
            return;
        }

        const currentOrg = getFromLocalStorage('current_org');
        if (!currentOrg?.org_id) {
            message.error('No se pudo identificar la organización actual');
            return;
        }

        setShowLoading(true);
        setLoadingText('Generando informe gerencial...');

        try {
            await axios.post(`${apiUrl}/informs/create-managerial-cross-report/`, {
                iash_evl_id: selectedIashEvaluationId,
                ipra_evl_id: selectedIpraEvaluationId,
            }, headers);

            const runs = await getManagerialCrossRuns(currentOrg.org_id);
            if (runs.length > 0) {
                await getManagerialCrossReport(runs[0].gcr_id, { silent: true });
            }

            message.success('Informe gerencial generado y guardado en BD');
        } catch (error) {
            const errorText = error?.response?.data?.error || 'No fue posible generar el informe gerencial';
            message.error(errorText);
        } finally {
            setShowLoading(false);
        }
    };

    const openManagerialCrossReport = (gcrId) => {
        if (!gcrId) return;

        const rootUrl = window.location.origin;
        window.open(`${rootUrl}/informes/gerencial/${gcrId}`, '_blank');
    };

    const managerialRunOptions = managerialRuns.map((row) => ({
        value: row.gcr_id,
        label: `${row.created_at ? new Date(row.created_at).toLocaleString() : '-'} · ${row.iash_evl_title} + ${row.ipra_evl_title}`,
    }));

    const managerialColumns = [
        {
            title: 'Fecha',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (value) => value ? new Date(value).toLocaleString() : '-',
        },
        {
            title: 'IASH',
            dataIndex: 'iash_evl_title',
            key: 'iash_evl_title',
        },
        {
            title: 'IPRA',
            dataIndex: 'ipra_evl_title',
            key: 'ipra_evl_title',
        },
        // {
        //     title: 'Estado',
        //     dataIndex: 'status',
        //     key: 'status',
        //     render: (value) => {
        //         const colorMap = {
        //             ready: 'green',
        //             processing: 'gold',
        //             draft: 'blue',
        //             error: 'red',
        //         };

        //         return <Tag color={colorMap[value] || 'default'}>{String(value || '').toUpperCase()}</Tag>;
        //     },
        // },
        // {
        //     title: 'ICP',
        //     dataIndex: ['kpis', 'cross_index'],
        //     key: 'cross_index',
        //     render: (value) => value ?? '-',
        // },
        // {
        //     title: 'Datos',
        //     key: 'data_quality',
        //     render: (_, row) => (
        //         row.has_real_data
        //             ? <Tag color='green'>DATOS REALES</Tag>
        //             : <Tag color='orange'>SIN DATOS</Tag>
        //     ),
        // },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, row) => (
                <div className='selfcare-managerial-table-actions'>
                    <Button type='link' onClick={() => getManagerialCrossReport(row.gcr_id)}>
                        Ver dashboard
                    </Button>
                    <Button type='link' onClick={() => openManagerialCrossReport(row.gcr_id)}>
                        Ver informe
                    </Button>
                </div>
            ),
        },
    ];

    const dashboardItems = [
        {
            key: 'general',
            label: 'Informe general',
            children: (
                <div className='selfcare-dashboard-pane'>
                    <div className='selfcare-dashboard-toolbar'>
                        <div className='selfcare-dashboard-toolbar-field'>
                            <div className='selfcare-managerial-label'>Evaluación</div>
                            <Select
                                style={{ width: '100%' }}
                                value={selectedEvaluation?.evl_id}
                                onChange={handleOnChangeEvaluation}
                                placeholder='Selecciona una evaluación'
                            >
                                {dataEvaluations.map((row) => (
                                    <Option key={row.evl_id} value={row.evl_id}>
                                        {row.evl_title}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div className='selfcare-dashboard-toolbar-actions'>
                            <Button type='primary' onClick={seeGeneralInform} disabled={!selectedEvaluation?.evl_id}>
                                Ver informe general completo
                            </Button>
                            <Button onClick={downloadEvaluationExcel} disabled={!selectedEvaluation?.evl_id}>
                                Descargar Excel
                            </Button>
                        </div>
                    </div>

                    {totalResults?.average_by_dimension ? (
                        <LeadershipSummary
                            data={totalResults}
                            globalAverage={getGlobalAverage(totalResults)}
                            title='Índice de seguridad y autocuidado'
                            redLight={selectedEvaluation?.evl_max_red_light}
                            yellowLight={selectedEvaluation?.evl_max_yellow_light}
                            isMain={true}
                        />
                    ) : (
                        <Empty description='La evaluación seleccionada no tiene resumen disponible' />
                    )}
                </div>
            ),
        },
        {
            key: 'managerial-dashboard',
            label: 'Dashboard gerencial',
            children: (
                <div className='selfcare-dashboard-pane'>
                    <div className='selfcare-cross-controls'>
                        <div className='selfcare-cross-controls-group'>
                            <div className='selfcare-cross-controls-field'>
                                <div className='selfcare-managerial-label'>Evaluación IASH</div>
                                <Select
                                    style={{ width: '100%' }}
                                    value={selectedIashEvaluationId}
                                    onChange={(value) => setSelectedIashEvaluationId(value)}
                                    placeholder='Selecciona evaluación IASH'
                                >
                                    {iashEvaluations.map((row) => (
                                        <Option key={row.evl_id} value={row.evl_id}>
                                            {row.evl_title}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className='selfcare-cross-controls-field'>
                                <div className='selfcare-managerial-label'>Evaluación IPRA</div>
                                <Select
                                    style={{ width: '100%' }}
                                    value={selectedIpraEvaluationId}
                                    onChange={(value) => setSelectedIpraEvaluationId(value)}
                                    placeholder='Selecciona evaluación IPRA'
                                >
                                    {ipraEvaluations.map((row) => (
                                        <Option key={row.evl_id} value={row.evl_id}>
                                            {row.evl_title}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className='selfcare-cross-controls-action'>
                                <Button type='primary' onClick={createManagerialCrossReport}>
                                    Generar cruce
                                </Button>
                            </div>
                        </div>

                        <div className='selfcare-cross-controls-divider' />

                        <div className='selfcare-cross-controls-group selfcare-cross-controls-group-saved'>
                            <div className='selfcare-cross-controls-field selfcare-cross-controls-field-wide'>
                                <div className='selfcare-managerial-label'>Cruce de evaluación guardado</div>
                                <Select
                                    style={{ width: '100%' }}
                                    value={selectedManagerialRunId}
                                    onChange={(value) => getManagerialCrossReport(value)}
                                    placeholder='Selecciona un cruce guardado'
                                    options={managerialRunOptions}
                                />
                            </div>
                            <div className='selfcare-cross-controls-action'>
                                <Button onClick={() => openManagerialCrossReport(selectedManagerialRunId)} disabled={!selectedManagerialRunId}>
                                    Abrir informe
                                </Button>
                            </div>
                        </div>
                    </div>

                    {managerialReportLoading ? (
                        <div className='selfcare-dashboard-inline-loading'>Cargando cruce de evaluación...</div>
                    ) : (
                        <ManagerialCrossDashboard
                            reportData={selectedManagerialReport}
                            orgLogo={managerialOrgLogo}
                            orgName={selectedManagerialReport?.org_name}
                            onOpenFullReport={() => openManagerialCrossReport(selectedManagerialRunId)}
                        />
                    )}
                </div>
            ),
        },
        {
            key: 'runs',
            label: 'Historial de cruces',
            children: (
                <Table
                    rowKey='gcr_id'
                    dataSource={managerialRuns}
                    columns={managerialColumns}
                    pagination={{ pageSize: 5 }}
                    onRow={(row) => ({
                        onClick: () => getManagerialCrossReport(row.gcr_id, { silent: true }),
                    })}
                />
            ),
        },
    ];

    const dashboardTitle = `${title} - Seguridad y Autocuidado`;

    return (
        <div className='main-container selfcare-dashboard-page'>
            {showLoading && <LoadingOverlay text={loadingText} showProgress={false} progress={null} />}

            {dataEvaluations.length === 0 && (
                <Content className='main-subcontainer main-subcontainer-column'>
                    <Title type='primary' level={4}>No se encontraron evaluaciones</Title>
                </Content>
            )}

            {dataEvaluations.length > 0 && (
                <Content className='main-subcontainer main-subcontainer-column'>
                    <div className='selfcare-dashboard-container'>
                        <div className='selfcare-dashboard-container-header'>
                            <Title type='primary' level={2}>{dashboardTitle}</Title>
                        </div>
                        <Tabs items={dashboardItems} className='selfcare-dashboard-tabs' />
                    </div>
                </Content>
            )}
        </div>
    );
};

export default SelfcareDashboardView;
